import React, { useEffect, useState, useTransition } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { MagnifyingGlass, Trash, X } from '@phosphor-icons/react';
import { Tooltip } from 'react-tooltip';
import { BounceLoader, MoonLoader } from 'react-spinners';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import colors from '@Common/assets/colors';
import RemoveAccountModal from '@Profile/modals/RemoveAccountModal';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import ValidateEmail from '@Utils/emailValidation';

interface Props {
  users: string[];
  loading: boolean;
}

const AdminAccessSettings: React.FC<Props> = ({ users, loading }: Props) => {
  const [userList, setUserList] = useState<string[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState('');
  const [isPending, startTransition] = useTransition();
  const [newUserEmail, setNewUserEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const userEmail = localStorage.getItem('email');
  const [search, setSearch] = useState('');

  const { logout } = useAuth();
  const account = accountsAPI(logout);

  const handleDelete = async (email: string) => {
    setIsDeleting(true);
    if (userList) {
      startTransition(() => {
        account
          .deleteUser(email)
          .then(() => {
            setUserList((prev) => prev!.filter((user) => user !== email));
            setEmailToDelete('');
            setMessage('User account removed ');
            setMessageType('success');
          })
          .catch((error) => {
            setMessage(`Error removing user: ${error.message}`);
            setMessageType('error');
          })
          .finally(() => {
            setIsDeleting(false);
            setShowConfirm(false);
          });
      });
    }
  };

  const handleAddUser = async () => {
    const validationError = ValidateEmail(newUserEmail);
    if (validationError) {
      setMessage(validationError.message);
      validationError.messageType === 'error' &&
        setMessageType(validationError.messageType);
      return;
    }

    setIsProcessing(true);
    try {
      await account.addUser(newUserEmail);
      setUserList((prev) => [newUserEmail, ...prev!]);
      setNewUserEmail('');
      setMessage('User added successfully');
      setMessageType('success');
    } catch (error) {
      setMessage(`${error}`);
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDelete = () => {
    handleDelete(emailToDelete);
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
  };

  useEffect(() => {
    if (userEmail) {
      const filteredUserList = users.filter(
        (user) =>
          user !== userEmail &&
          user.toLowerCase().includes(search.toLowerCase())
      );
      setUserList(filteredUserList);
    }
  }, [users, userEmail, search]);

  return (
    <>
      <RemoveAccountModal
        emailToDelete={emailToDelete}
        handleCancelDelete={handleCancelDelete}
        handleConfirmDelete={handleConfirmDelete}
        isDeleting={isDeleting}
        isOpen={showConfirm}
        onClose={handleCancelDelete}
      />
      <div className="flex h-full w-full flex-col gap-3 p-2">
        <div className="flex flex-col">
          <div className="flex w-full flex-col">
            <div className="flex w-full flex-col">
              <div className="flex flex-col gap-1 pb-5">
                <h2 className="text-xl font-semibold text-blue-900">
                  Manage User Access
                </h2>
                <p className="text-sm text-blue-900">
                  Here you can limit users who access the application. These
                  settings are associated to users access.
                </p>
              </div>

              <div className="flex w-full flex-col rounded-lg border border-gray-400 bg-[#fbfcff] p-4">
                <div className="flex flex-col text-sm font-medium text-gray-700">
                  <div className="flex flex-wrap items-center gap-3 rounded p-1 px-2 pb-3 hover:bg-[#edf7ff]">
                    <label className="flex w-full justify-center py-1 text-lg text-primary-600 sm:w-[220px] sm:text-left">
                      Add User
                    </label>
                    <div className="flex flex-grow sm:w-auto">
                      <input
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="Input User Email"
                        className={`w-full flex-grow rounded-md border border-gray-300 p-2 py-1 text-gray-600 outline-none sm:w-auto`}
                      />
                      {isProcessing ? (
                        <div className="flex items-center justify-center px-2">
                          <BounceLoader
                            size={20}
                            color={colors.brandWarning[500]}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={handleAddUser}
                          disabled={isPending || isProcessing}
                        >
                          <div
                            className={`ml-2 flex items-center justify-center rounded bg-primary-700 p-1 px-2 text-white transition hover:bg-blue-500 ${
                              isPending || isProcessing
                                ? 'cursor-not-allowed opacity-50'
                                : ''
                            }`}
                            data-tooltip-id="add-user"
                            data-tooltip-content="Add user"
                          >
                            <FaPlus className="" size={16} />
                            <Tooltip id="add-user" />
                          </div>
                        </button>
                      )}
                    </div>
                    {/* Searchbar */}
                    <div className="ml-11 flex w-full flex-row items-center justify-between rounded-md border bg-white px-2 py-1 md:w-auto">
                      <div className="flex items-center gap-x-2">
                        <MagnifyingGlass size={20} weight="bold" />
                        <input
                          type="text"
                          className="border-gray-200 font-inter text-sm focus:outline-none"
                          placeholder="Search Email"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>
                      <X
                        data-testid="test-generated-search-clear-btn"
                        weight="bold"
                        className="cursor-pointer hover:text-brandRed-500"
                        onClick={() => setSearch('')}
                      />
                    </div>
                  </div>
                  {/* Message Section */}
                  <div className="flex flex-col rounded-md border border-gray-300 p-3 sm:p-5">
                    {messageType === 'error' && (
                      <div className="flex items-center justify-end pr-[50px] font-bold text-red-500">
                        <Warning size={20} weight="bold" className="mr-1" />
                        <span>{message}</span>
                      </div>
                    )}
                    {messageType === 'success' && (
                      <div className="flex items-center justify-end pr-[50px] font-bold text-green-500">
                        <CheckCircle
                          size={20}
                          weight="bold"
                          className="mr-1 text-green-500"
                        />
                        <span>
                          User{' '}
                          <span
                            className={`${
                              message === 'User added successfully'
                                ? 'text-green-500'
                                : 'text-red-500'
                            }`}
                          >
                            {message === 'User added successfully'
                              ? 'added'
                              : 'removed'}
                          </span>{' '}
                          successfully
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between rounded p-1 px-4 sm:px-10 md:flex-row">
                      <div className="px-2 py-1">Emails</div>
                      <div className="py-1 text-right">Actions</div>
                    </div>
                    <div className="divider border"></div>
                    <div className="flex max-h-[30vh] flex-col overflow-y-auto rounded p-1 px-4 sm:flex-row sm:px-10">
                      <div className="flex flex-grow">
                        <div className="w-full">
                          <div className="mb-2 w-full rounded text-left text-sm font-medium text-gray-400">
                            <div>
                              {loading ? (
                                <div className="flex flex-col items-center justify-center gap-y-6 p-5">
                                  <MoonLoader size={50} color="#071D49" />
                                  <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
                                    User List Loading...
                                  </span>
                                </div>
                              ) : (
                                <>
                                  {userList && userList.length > 0 ? (
                                    userList.map((item, index) => (
                                      <div
                                        key={index}
                                        className="flex w-full items-center rounded border-b border-gray-200 px-3 py-2 font-normal text-black hover:bg-primary-700 hover:text-white"
                                      >
                                        {/* Email text */}
                                        <div className="sm:truncate-none flex-grow truncate text-sm sm:whitespace-normal">
                                          {item}
                                        </div>

                                        {/* Trash icon */}
                                        <div className="ml-2 flex-shrink-0">
                                          <button
                                            type="button"
                                            className="inline-flex items-center rounded px-2 py-1 text-xs font-bold leading-none hover:text-white"
                                            onClick={() => {
                                              setShowConfirm(true);
                                              setEmailToDelete(item);
                                            }}
                                          >
                                            <Trash size={24} />
                                          </button>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="pt-7 text-center">
                                      No users found
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col text-red-600">
            <span className="flex flex-row items-center gap-1 text-xs font-bold sm:text-base">
              Note:
            </span>
            <span className="mt-2 text-justify text-xs font-semibold sm:text-sm">
              The prompt you enter will be appended to a pre-determined user
              story and format to ensure consistency in the generated test
              cases. Please make sure to include {'{user_story}'} and{' '}
              {'{test_collection}'} placeholders.
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminAccessSettings;
